import React, { useRef, useEffect, useState } from 'react';
import './PalmetaGame.css';
import BeltPromotionModal from './BeltPromotionModal';
import PosePreloader from './PosePreloader';
import usePoseLoader from './hooks/usePoseLoader';
import { useEquipmentImages } from './hooks/useEquipmentImages';
import { drawOptimizedSkeleton, drawOptimizedGloves, calculateShoulderDistance } from './utils/skeletonUtils';
import useGameScreen from './hooks/useGameScreen';

const OBSTACLE_TYPES = {
  PALMETA_RIGHT: 'palmeta_right',
  PALMETA_LEFT: 'palmeta_left',
  PALMETA_SWITCH: 'palmeta_switch',
  BAR_DIAGONAL: 'bar_diagonal',
  BAR_HIGH: 'bar_high'
};

function PalmetaGame({ onClose, userData }) {
  useGameScreen();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const [lives, setLives] = useState(3);
  const livesRef = useRef(3);
  const [combo, setCombo] = useState(0);
  const comboRef = useRef(0);
  const [countdown, setCountdown] = useState(3);
  const [isReady, setIsReady] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const gameOverRef = useRef(false);
  const [showBeltModal, setShowBeltModal] = useState(false);
  const [beltPromotionData, setBeltPromotionData] = useState(null);
  const [showEnergyWarning, setShowEnergyWarning] = useState(false);
  const [currentHint, setCurrentHint] = useState('');

  const obstacles = useRef([]);
  const particles = useRef([]);
  const prevHand = useRef({ r: [0, 0], l: [0, 0] });
  const prevNose = useRef({ x: 0.5, y: 0.5 });
  const spawnTimer = useRef(null);
  const speedRef = useRef(0.04);
  const spawnDelayRef = useRef(500);
  const difficultyTimer = useRef(null);
  const sequenceQueue = useRef([]);
  const sequenceLevel = useRef(0);
  const [level, setLevel] = useState(1);
  const sequencePause = useRef(false);
  const equipmentImages = useEquipmentImages();
  const audioRef = useRef(null);
  const onResultsRef = useRef(null);
  const energyCost = 5;
  const flashRef = useRef({ active: false, color: '', time: 0 });
  const feedbackRef = useRef({ active: false, text: '', color: '', x: 0, y: 0, time: 0 });

  const images = useRef({
    gloveHandDerecha: new Image(),
    gloveHandIzquierda: new Image(),
    gloveFoot: new Image(),
    background: new Image(),
    playerHead: new Image(),
    playerBody: new Image(),
    armImage: new Image(),
    leftArmImage: new Image(),
    rightLegImage: new Image(),
    leftLegImage: new Image(),
    palmetaRoja: new Image(),
    palmetaAzul: new Image(),
    palmetaRojaCostado: new Image(),
    palmetaAzulCostado: new Image()
  });

  useEffect(() => {
    if (!equipmentImages.loading) {
      images.current.gloveHandDerecha.src = equipmentImages.gloveHandDerecha;
      images.current.gloveHandIzquierda.src = equipmentImages.gloveHandIzquierda;
      images.current.gloveFoot.src = equipmentImages.gloveFoot;
      images.current.playerHead.src = equipmentImages.playerHead;
    }
    images.current.background.src = '/img/backgroundpalmetas.png';
    images.current.playerBody.src = '/img/rotura/traje.png';
    images.current.armImage.src = '/img/rotura/brazoderecho.png';
    images.current.leftArmImage.src = '/img/rotura/brazoizquierdo.png';
    images.current.rightLegImage.src = '/img/rotura/piederecho.png';
    images.current.leftLegImage.src = '/img/rotura/pieizquierdo.png';
    images.current.palmetaRoja.src = '/img/palmeta_roja.png';
    images.current.palmetaAzul.src = '/img/palmeta_azul.png';
    images.current.palmetaRojaCostado.src = '/img/palmeta_roja_costado.png';
    images.current.palmetaAzulCostado.src = '/img/palmeta_azul_costado.png';
  }, [equipmentImages]);

  // Secuencias predefinidas de combos (se eligen aleatoriamente)
  const SEQUENCES = [
    // Básicas
    ['R', 'R', 'L'],
    ['L', 'L', 'R'],
    ['R', 'L', 'R'],
    ['L', 'R', 'L'],
    // Con agacharse
    ['R', 'R', 'L', 'H'],
    ['L', 'R', 'H', 'R'],
    ['R', 'H', 'L', 'L'],
    // Con esquiva
    ['R', 'DR', 'L'],
    ['L', 'DL', 'R'],
    ['R', 'L', 'DR', 'R'],
    ['L', 'R', 'DL', 'L'],
    // Con gancho
    ['R', 'SR', 'L'],
    ['L', 'SL', 'R'],
    ['R', 'R', 'SL', 'L'],
    ['L', 'L', 'SR', 'R'],
    // Combos largos
    ['R', 'R', 'L', 'H', 'R'],
    ['L', 'R', 'DR', 'L', 'L'],
    ['R', 'L', 'SL', 'H', 'R'],
    ['L', 'DL', 'R', 'R', 'SR'],
    // Combos difíciles
    ['R', 'L', 'R', 'L', 'H', 'DR'],
    ['L', 'R', 'SL', 'R', 'DL', 'L'],
    ['R', 'R', 'H', 'L', 'L', 'SR', 'R'],
    ['L', 'DR', 'R', 'SL', 'H', 'R', 'L'],
  ];

  const pickSequence = () => {
    const lvl = sequenceLevel.current;
    // A mayor nivel, acceso a secuencias más largas
    const maxIdx = Math.min(4 + lvl * 2, SEQUENCES.length);
    const pool = SEQUENCES.slice(0, maxIdx);
    return [...pool[Math.floor(Math.random() * pool.length)]];
  };

  const mapSeqToObstacle = (code) => {
    const now = Date.now();
    const base = {
      id: now + Math.random(),
      z: 1.0,
      hit: false,
      missed: false,
      switchSide: false,
      switched: false,
      switchAt: null,
      diagDir: 1,
      spawnTime: now
    };
    switch (code) {
      case 'R': return { ...base, type: OBSTACLE_TYPES.PALMETA_RIGHT, side: 'right' };
      case 'L': return { ...base, type: OBSTACLE_TYPES.PALMETA_LEFT, side: 'left' };
      case 'SR': return { ...base, type: OBSTACLE_TYPES.PALMETA_SWITCH, side: 'left', switchSide: true, switchAt: 0.35 + Math.random() * 0.15 };
      case 'SL': return { ...base, type: OBSTACLE_TYPES.PALMETA_SWITCH, side: 'right', switchSide: true, switchAt: 0.35 + Math.random() * 0.15 };
      case 'DR': return { ...base, type: OBSTACLE_TYPES.BAR_DIAGONAL, side: 'right', diagDir: 1 };
      case 'DL': return { ...base, type: OBSTACLE_TYPES.BAR_DIAGONAL, side: 'left', diagDir: -1 };
      case 'H': return { ...base, type: OBSTACLE_TYPES.BAR_HIGH, side: null };
      default: return { ...base, type: OBSTACLE_TYPES.PALMETA_RIGHT, side: 'right' };
    }
  };

  const spawnNext = () => {
    if (gameOverRef.current) return;
    if (sequenceQueue.current.length === 0) {
      sequenceQueue.current = pickSequence();
      // Pausa entre secuencias: insertar un null que se skipea
      sequencePause.current = true;
      return;
    }
    const code = sequenceQueue.current.shift();
    obstacles.current.push(mapSeqToObstacle(code));
  };

  const createParticles = (x, y, color, count = 15) => {
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count;
      const speed = 3 + Math.random() * 4;
      particles.current.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2,
        life: 1,
        color,
        size: 3 + Math.random() * 5
      });
    }
  };

  const loseLife = (x, y) => {
    livesRef.current = Math.max(livesRef.current - 1, 0);
    setLives(livesRef.current);
    flashRef.current = { active: true, color: 'rgba(255,0,0,0.4)', time: Date.now() };
    feedbackRef.current = { active: true, text: '¡FALLO!', color: '#FF0000', x: x || 320, y: y || 240, time: Date.now() };
    if (livesRef.current <= 0) {
      gameOverRef.current = true;
      setGameOver(true);
      handleGameEnd();
    }
  };

  const addScore = (points, text, x, y, color) => {
    comboRef.current += 1;
    setCombo(comboRef.current);
    const multiplier = comboRef.current >= 10 ? 3 : comboRef.current >= 5 ? 2 : 1;
    const total = points * multiplier;
    scoreRef.current += total;
    setScore(scoreRef.current);
    flashRef.current = { active: true, color: 'rgba(0,255,0,0.2)', time: Date.now() };
    feedbackRef.current = { active: true, text: `${text} +${total}`, color, x, y, time: Date.now() };
  };

  const resetCombo = () => {
    comboRef.current = 0;
    setCombo(0);
  };

  const drawPalmeta = (ctx, obs, w, h) => {
    // Perspectiva: z=1 lejos, z=0 cerca
    const scale = 1 - obs.z * 0.7;
    const centerX = w * 0.5;
    const centerY = h * 0.5;

    // Posición lateral según lado
    const sideOffset = obs.side === 'right' ? -w * 0.18 : w * 0.18;
    const x = centerX + sideOffset * scale;
    const y = centerY - h * 0.05 * scale;

    // Tamaño crece al acercarse
    const size = 40 + 60 * scale;

    const isRight = obs.side === 'right';
    const isCostado = obs.switchSide && obs.switched;

    // Seleccionar imagen
    let palmetaImg;
    if (isCostado) {
      palmetaImg = isRight ? images.current.palmetaAzulCostado : images.current.palmetaRojaCostado;
    } else {
      palmetaImg = isRight ? images.current.palmetaRoja : images.current.palmetaAzul;
    }

    ctx.save();
    ctx.translate(x, y);
    ctx.globalAlpha = 0.3 + 0.7 * scale;

    const pw = size * 1.4;
    const ph = size * 1.1;

    if (palmetaImg?.complete && palmetaImg.naturalWidth > 0) {
      ctx.shadowBlur = 15 * scale;
      ctx.shadowColor = isRight ? '#FF2222' : '#2266FF';
      ctx.scale(-1, 1);
      ctx.drawImage(palmetaImg, -pw / 2, -ph / 2, pw, ph);
      ctx.scale(-1, 1);
    } else {
      // Fallback canvas
      const baseColor = isRight ? '#FF2222' : '#2266FF';
      ctx.fillStyle = baseColor;
      ctx.beginPath();
      ctx.ellipse(0, 0, pw / 2, ph / 2, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Indicador de mano
    ctx.shadowBlur = 0;
    ctx.globalAlpha = scale;
    ctx.save();
    ctx.scale(-1, 1);
    ctx.fillStyle = '#fff';
    ctx.font = `bold ${16 * scale}px Arial`;
    ctx.textAlign = 'center';
    if (obs.switchSide && obs.switched) {
      ctx.fillText(isRight ? '👊 GANCHO DER' : '👊 GANCHO IZQ', 0, -ph / 2 - 10);
    } else if (obs.switchSide) {
      ctx.fillText('⟷ ATENCIÓN', 0, -ph / 2 - 10);
    } else {
      ctx.fillText(isRight ? '👊 DERECHA' : '👊 IZQUIERDA', 0, -ph / 2 - 10);
    }
    ctx.restore();

    ctx.restore();

    return { x, y, radius: size * 0.5 * scale };
  };

  const drawBarDiagonal = (ctx, obs, w, h) => {
    const scale = 1 - obs.z * 0.7;
    const centerX = w * 0.5;
    const centerY = h * 0.45;

    const barLen = w * 0.4 * scale;
    const angle = obs.diagDir * (Math.PI / 4); // 45 grados

    const x = centerX;
    const y = centerY;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.globalAlpha = 0.3 + 0.7 * scale;

    // Barra con gradiente
    const grad = ctx.createLinearGradient(-barLen / 2, 0, barLen / 2, 0);
    grad.addColorStop(0, '#FF8800');
    grad.addColorStop(0.5, '#FFAA00');
    grad.addColorStop(1, '#FF8800');
    ctx.fillStyle = grad;

    const barH = 22 * scale;
    ctx.shadowBlur = 25 * scale;
    ctx.shadowColor = '#FF8800';
    ctx.fillRect(-barLen / 2, -barH / 2, barLen, barH);

    // Bordes brillantes
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 3;
    ctx.strokeRect(-barLen / 2, -barH / 2, barLen, barH);

    // Texto indicador
    ctx.shadowBlur = 0;
    ctx.rotate(-angle);
    ctx.scale(-1, 1);
    ctx.fillStyle = '#FFD700';
    ctx.font = `bold ${16 * scale}px Arial`;
    ctx.textAlign = 'center';
    const diagLabel = obs.diagDir > 0 ? '↩ ESQUIVA DER' : '↪ ESQUIVA IZQ';
    ctx.fillText(diagLabel, 0, -barH - 15 * scale);

    ctx.restore();

    return { x, y, barLen: barLen * 0.5, angle, scale };
  };

  const drawBarHigh = (ctx, obs, w, h) => {
    const scale = 1 - obs.z * 0.7;
    const centerX = w * 0.5;
    const y = h * 0.32;

    const barLen = w * 0.5 * scale;

    ctx.save();
    ctx.translate(centerX, y);
    ctx.globalAlpha = 0.3 + 0.7 * scale;

    const grad = ctx.createLinearGradient(-barLen / 2, 0, barLen / 2, 0);
    grad.addColorStop(0, '#FF0066');
    grad.addColorStop(0.5, '#FF3399');
    grad.addColorStop(1, '#FF0066');
    ctx.fillStyle = grad;

    const barH = 24 * scale;
    ctx.shadowBlur = 25 * scale;
    ctx.shadowColor = '#FF0066';
    ctx.fillRect(-barLen / 2, -barH / 2, barLen, barH);

    ctx.strokeStyle = '#FF99CC';
    ctx.lineWidth = 3;
    ctx.strokeRect(-barLen / 2, -barH / 2, barLen, barH);

    ctx.shadowBlur = 0;
    ctx.scale(-1, 1);
    ctx.fillStyle = '#FF99CC';
    ctx.font = `bold ${16 * scale}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText('⬇ AGACHATE', 0, -barH - 12 * scale);

    ctx.restore();

    return { x: centerX, y, barLen: barLen * 0.5, scale };
  };

  const detectPalmetaHit = (landmarks, obs, hitbox, w, h) => {
    if (!landmarks || obs.hit || obs.missed) return false;
    if (obs.z > 0.25) return false; // Todavía lejos

    const rHand = landmarks[20];
    const lHand = landmarks[19];
    const rHandX = w * rHand.x;
    const rHandY = h * rHand.y;
    const lHandX = w * lHand.x;
    const lHandY = h * lHand.y;

    const speedR = Math.sqrt(
      (rHand.x - prevHand.current.r[0]) ** 2 +
      (rHand.y - prevHand.current.r[1]) ** 2
    );
    const speedL = Math.sqrt(
      (lHand.x - prevHand.current.l[0]) ** 2 +
      (lHand.y - prevHand.current.l[1]) ** 2
    );

    const needRight = obs.side === 'right';
    const handX = needRight ? rHandX : lHandX;
    const handY = needRight ? rHandY : lHandY;
    const speed = needRight ? speedR : speedL;

    const dist = Math.sqrt((handX - hitbox.x) ** 2 + (handY - hitbox.y) ** 2);

    if (dist < hitbox.radius + 30 && speed > 0.015) {
      // Verificar que usó la mano correcta
      const wrongHand = needRight ? 
        Math.sqrt((lHandX - hitbox.x) ** 2 + (lHandY - hitbox.y) ** 2) < dist :
        Math.sqrt((rHandX - hitbox.x) ** 2 + (rHandY - hitbox.y) ** 2) < dist;
      
      if (!wrongHand || dist < hitbox.radius) {
        return true;
      }
    }
    return false;
  };

  const detectBarDodge = (landmarks, obs, h) => {
    if (!landmarks || obs.hit || obs.missed) return false;
    if (obs.z > 0.2) return false;

    const nose = landmarks[0];
    const lShoulder = landmarks[11];
    const rShoulder = landmarks[12];

    if (obs.type === OBSTACLE_TYPES.BAR_DIAGONAL) {
      // Torsión de torso: diferencia de profundidad/posición Y entre hombros
      const shoulderDiffY = Math.abs(lShoulder.y - rShoulder.y);
      const shoulderDiffX = Math.abs(lShoulder.x - rShoulder.x);
      // Si los hombros están desalineados verticalmente = torsión
      return shoulderDiffY > 0.04 || shoulderDiffX < 0.06;
    }

    if (obs.type === OBSTACLE_TYPES.BAR_HIGH) {
      // Agacharse: nariz por debajo de cierto umbral
      return nose.y > 0.55;
    }

    return false;
  };

  const onResults = (results) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || video.videoWidth === 0) return;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const w = canvas.width;
    const h = canvas.height;

    ctx.save();
    ctx.clearRect(0, 0, w, h);

    // Fondo
    if (images.current.background.complete) {
      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(images.current.background, -w, 0, w, h);
      ctx.restore();
    }

    // Actualizar y dibujar obstáculos
    const now = Date.now();
    obstacles.current = obstacles.current.filter(obs => {
      if (obs.hit) return false;

      // Mover hacia el jugador
      obs.z -= speedRef.current;

      // Switch de lado para palmetas que cambian
      if (obs.switchSide && !obs.switched && obs.z <= obs.switchAt) {
        obs.switched = true;
        obs.side = obs.side === 'right' ? 'left' : 'right';
      }

      // Pasó de largo = fallo
      if (obs.z <= -0.1) {
        if (!obs.missed) {
          obs.missed = true;
          // Solo barras quitan vida, palmetas no
          if (obs.type === OBSTACLE_TYPES.BAR_DIAGONAL || obs.type === OBSTACLE_TYPES.BAR_HIGH) {
            const fx = obs.type === OBSTACLE_TYPES.BAR_HIGH ? w * 0.5 : 
                       obs.side === 'right' ? w * 0.6 : w * 0.4;
            const fy = obs.type === OBSTACLE_TYPES.BAR_HIGH ? h * 0.35 : h * 0.5;
            loseLife(fx, fy);
          }
          resetCombo();
        }
        return false;
      }

      // Dibujar según tipo
      let hitbox = null;
      if (obs.type === OBSTACLE_TYPES.PALMETA_RIGHT ||
          obs.type === OBSTACLE_TYPES.PALMETA_LEFT ||
          obs.type === OBSTACLE_TYPES.PALMETA_SWITCH) {
        hitbox = drawPalmeta(ctx, obs, w, h);
      } else if (obs.type === OBSTACLE_TYPES.BAR_DIAGONAL) {
        hitbox = drawBarDiagonal(ctx, obs, w, h);
      } else if (obs.type === OBSTACLE_TYPES.BAR_HIGH) {
        hitbox = drawBarHigh(ctx, obs, w, h);
      }

      // Detectar interacción
      if (results.poseLandmarks && hitbox && !gameOverRef.current) {
        if (obs.type.startsWith('palmeta')) {
          if (detectPalmetaHit(results.poseLandmarks, obs, hitbox, w, h)) {
            obs.hit = true;
            const points = obs.switchSide ? 20 : 10;
            const label = obs.switchSide ? '¡GANCHO!' : '¡BIEN!';
            const clr = obs.side === 'right' ? '#FF4444' : '#4488FF';
            addScore(points, label, hitbox.x, hitbox.y, clr);
            createParticles(hitbox.x, hitbox.y, clr, 20);
            return false;
          }
        } else {
          if (detectBarDodge(results.poseLandmarks, obs, h)) {
            obs.hit = true;
            const dodgeLabel = obs.type === OBSTACLE_TYPES.BAR_HIGH ? '¡ESQUIVADO!' : '¡ESQUIVA!';
            addScore(15, dodgeLabel, w * 0.5, h * 0.4, '#FFAA00');
            createParticles(w * 0.5, h * 0.4, '#FFAA00', 15);
            return false;
          }
        }
      }

      return true;
    });

    // Dibujar esqueleto y guantes
    if (results.poseLandmarks) {
      const shoulderDist = drawOptimizedSkeleton(ctx, results.poseLandmarks, w, h, images.current);
      if (shoulderDist) {
        drawOptimizedGloves(ctx, results.poseLandmarks, w, h, images.current, shoulderDist);
      }

      // Actualizar previos
      prevHand.current = {
        r: [results.poseLandmarks[20].x, results.poseLandmarks[20].y],
        l: [results.poseLandmarks[19].x, results.poseLandmarks[19].y]
      };
      prevNose.current = {
        x: results.poseLandmarks[0].x,
        y: results.poseLandmarks[0].y
      };
    }

    // Partículas
    particles.current = particles.current.filter(p => {
      p.x += p.vx; p.y += p.vy; p.vy += 0.3; p.life -= 0.02; p.size *= 0.97;
      return p.life > 0;
    });
    particles.current.forEach(p => {
      ctx.globalAlpha = p.life;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalAlpha = 1;

    // Flash de daño/acierto
    if (flashRef.current.active) {
      const elapsed = now - flashRef.current.time;
      if (elapsed < 300) {
        ctx.fillStyle = flashRef.current.color;
        ctx.fillRect(0, 0, w, h);
      } else {
        flashRef.current.active = false;
      }
    }

    // Feedback flotante de acierto
    if (feedbackRef.current.active) {
      const elapsed = now - feedbackRef.current.time;
      if (elapsed < 800) {
        const progress = elapsed / 800;
        const yOff = progress * 80;
        const scale = 1 + progress * 0.3;
        const opacity = 1 - progress;
        ctx.save();
        ctx.scale(-1, 1);
        ctx.globalAlpha = opacity;
        ctx.shadowColor = feedbackRef.current.color;
        ctx.shadowBlur = 30;
        ctx.fillStyle = feedbackRef.current.color;
        ctx.font = `bold ${40 * scale}px Arial`;
        ctx.textAlign = 'center';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 4;
        ctx.strokeText(feedbackRef.current.text, -feedbackRef.current.x, feedbackRef.current.y - yOff);
        ctx.fillText(feedbackRef.current.text, -feedbackRef.current.x, feedbackRef.current.y - yOff);
        ctx.restore();
      } else {
        feedbackRef.current.active = false;
      }
    }

    // Hint del próximo obstáculo
    const nextObs = obstacles.current.find(o => !o.hit && !o.missed && o.z > 0.3);
    if (nextObs) {
      let hint = '';
      if (nextObs.type === OBSTACLE_TYPES.PALMETA_RIGHT) hint = '👊 DERECHA';
      else if (nextObs.type === OBSTACLE_TYPES.PALMETA_LEFT) hint = '👊 IZQUIERDA';
      else if (nextObs.type === OBSTACLE_TYPES.PALMETA_SWITCH) {
        hint = nextObs.switched ? 
          (nextObs.side === 'right' ? '👊 GANCHO DERECHO' : '👊 GANCHO IZQUIERDO') : 
          '⟷ ATENCIÓN';
      }
      else if (nextObs.type === OBSTACLE_TYPES.BAR_DIAGONAL) hint = nextObs.diagDir > 0 ? '↩ ESQUIVA DER' : '↪ ESQUIVA IZQ';
      else if (nextObs.type === OBSTACLE_TYPES.BAR_HIGH) hint = '⬇ AGACHATE';
      setCurrentHint(hint);
    }

    ctx.restore();
  };

  onResultsRef.current = onResults;
  const poseLoader = usePoseLoader((...args) => onResultsRef.current?.(...args));

  // Iniciar
  useEffect(() => {
    const checkEnergy = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/training/start`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ energyCost })
        });
        if (!response.ok) {
          setShowEnergyWarning(true);
          setTimeout(onClose, 3000);
          return;
        }
      } catch (error) {
        console.error('Error al verificar energía:', error);
      }
    };
    checkEnergy();
    if (videoRef.current) poseLoader.start(videoRef.current);
    return () => {
      poseLoader.stop();
      if (spawnTimer.current) clearInterval(spawnTimer.current);
      if (difficultyTimer.current) clearInterval(difficultyTimer.current);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    };
  }, []);

  // Countdown y spawn
  useEffect(() => {
    if (!poseLoader.loaded) return;
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {});
    }
    const countdownTimer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          setIsReady(true);

          // Spawn inmediato de la primera palmeta
          spawnNext();

          // Spawn de obstáculos por secuencia
          spawnTimer.current = setInterval(() => {
            if (gameOverRef.current) return;
            if (sequencePause.current) {
              sequencePause.current = false;
              return; // skip un tick como pausa
            }
            spawnNext();
          }, spawnDelayRef.current);

          // Incrementar dificultad cada 15s
          difficultyTimer.current = setInterval(() => {
            if (gameOverRef.current) return;
            sequenceLevel.current += 1;
            setLevel(sequenceLevel.current + 1);
            speedRef.current = Math.min(speedRef.current + 0.003, 0.065);
            spawnDelayRef.current = Math.max(spawnDelayRef.current - 40, 250);
            clearInterval(spawnTimer.current);
            spawnTimer.current = setInterval(() => {
              if (gameOverRef.current) return;
              if (sequencePause.current) {
                sequencePause.current = false;
                return;
              }
              spawnNext();
            }, spawnDelayRef.current);
          }, 12000);

          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      clearInterval(countdownTimer);
    };
  }, [poseLoader.loaded]);

  const handleGameEnd = async () => {
    if (spawnTimer.current) clearInterval(spawnTimer.current);
    if (difficultyTimer.current) clearInterval(difficultyTimer.current);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/training/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          score: scoreRef.current,
          tiempo: 0,
          precision: 0,
          modo: 'palmetas'
        })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.subiDeNivel && data.beltData) {
          setBeltPromotionData({ cinturon: data.cinturon, beltData: data.beltData });
          setTimeout(() => setShowBeltModal(true), 2500);
        }
      }
    } catch (error) {
      console.error('Error al guardar palmetas:', error);
    }
  };

  const handleCloseBeltModal = () => {
    setShowBeltModal(false);
    setBeltPromotionData(null);
    if (onClose) onClose();
  };

  return (
    <div className="palmeta-overlay">
      <div className="palmeta-container">
        <audio ref={audioRef} src="/audio/palmetasmusica.mp3" loop />
        <video ref={videoRef} style={{ display: 'none' }} />
        <canvas ref={canvasRef} className="palmeta-canvas" />

        {!poseLoader.loaded && (
          <PosePreloader progress={poseLoader.progress} statusText={poseLoader.statusText} title="PALMETAS" hint="Dejá el celular en un lugar fijo sin que se caiga. Alejate hasta que te tome el cuerpo completo. Golpeá las palmetas con la mano correcta (roja=derecha, azul=izquierda). Agachate con la barra rosa, girá el torso con las naranjas." />
        )}

        <button className="palmeta-close-btn" onClick={onClose}>✕</button>

        <div className="palmeta-header">
          <div className="palmeta-stat">{'❤️'.repeat(Math.max(0, lives))}{'🖤'.repeat(Math.max(0, 3 - lives))}</div>
          <div className="palmeta-stat">🥋 Nivel {level}</div>
          <div className="palmeta-stat">🎯 {score}</div>
          {combo >= 3 && <div className="palmeta-stat palmeta-combo">🔥 x{combo}</div>}
        </div>

        {currentHint && isReady && !gameOver && (
          <div className="palmeta-hint">{currentHint}</div>
        )}

        {!isReady && countdown > 0 && (
          <div className="palmeta-countdown-overlay">
            <div className="palmeta-countdown-number">{countdown}</div>
          </div>
        )}

        {showEnergyWarning && (
          <div className="palmeta-game-over">
            <h3>⚠️ ENERGÍA INSUFICIENTE</h3>
            <p>Necesitas {energyCost} de energía para jugar</p>
          </div>
        )}

        {gameOver && !showBeltModal && !beltPromotionData && (
          <div className="palmeta-game-over">
            <h3>¡GAME OVER!</h3>
            <h1>{score}</h1>
            <p>puntos</p>
            <button className="palmeta-menu-btn" onClick={onClose}>VOLVER</button>
          </div>
        )}

        {showBeltModal && beltPromotionData && (
          <BeltPromotionModal
            cinturon={beltPromotionData.cinturon}
            beltData={beltPromotionData.beltData}
            onClose={handleCloseBeltModal}
          />
        )}
      </div>
    </div>
  );
}

export default PalmetaGame;
